import type { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import React, { useMemo, useState } from "react";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaSelectionBar from "../../../../ui-kit/kuika-selection-bar/kuika-selection-bar";
import KuikaTable, { KuikaTableColumn } from "../../../../ui-kit/kuika-table/kuika-table";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";

interface ISelectionRow {
  id: string;
  name: string;
  role: string;
  status: "active" | "invited" | "archived";
}

const ROLES: string[] = ["Engineer", "Designer", "Product Manager", "QA", "Support"];
const STATUSES: ISelectionRow["status"][] = ["active", "invited", "archived"];

const ROWS: ISelectionRow[] = Array.from({ length: 25 }, (_, index: number): ISelectionRow => {
  const rowNumber: number = index + 1;
  return {
    id: String(rowNumber),
    name: `Member ${rowNumber}`,
    role: ROLES[index % ROLES.length],
    status: STATUSES[index % STATUSES.length]
  };
});

const COLUMNS: KuikaTableColumn<ISelectionRow>[] = [
  { id: "name", header: (): string => "Name", cell: (row: ISelectionRow): string => row.name, accessorKey: "name" },
  { id: "role", header: (): string => "Role", cell: (row: ISelectionRow): string => row.role, accessorKey: "role" },
  {
    id: "status",
    header: (): string => "Status",
    cell: (row: ISelectionRow): string => row.status,
    accessorKey: "status"
  }
];

const countSelected = (rowSelection: RowSelectionState): number =>
  Object.keys(rowSelection).filter((key: string): boolean => rowSelection[key]).length;

/** A table whose scroll container's last child is the bar — it floats over the rows, not below them. */
const TableBoundDemo: React.FC = (): JSX.Element => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = (updater): void => {
    setRowSelection((previous: RowSelectionState): RowSelectionState =>
      typeof updater === "function" ? updater(previous) : updater
    );
  };

  const count: number = useMemo((): number => countSelected(rowSelection), [rowSelection]);

  return (
    <div style={{ width: "100%", height: 320, overflowY: "auto" }}>
      <KuikaTable<ISelectionRow>
        data={ROWS}
        columns={COLUMNS}
        rowKey={(row: ISelectionRow): string => row.id}
        enableRowSelection
        enablePagination={false}
        rowSelection={rowSelection}
        onRowSelectionChange={handleRowSelectionChange}
      />
      <KuikaSelectionBar
        count={count}
        actions={
          <>
            <KuikaButton type="secondary" size="sm">
              Export
            </KuikaButton>
            <KuikaButton type="secondary" color="danger" size="sm">
              Delete
            </KuikaButton>
          </>
        }
        onClear={(): void => setRowSelection({})}
      />
    </div>
  );
};

/** No table involved — the count is driven directly by the +/- buttons. */
const StandaloneDemo: React.FC = (): JSX.Element => {
  const [count, setCount] = useState<number>(0);

  return (
    <div style={{ width: "100%", height: 160, overflowY: "auto" }}>
      <div style={{ height: 260, padding: 16, display: "flex", gap: 8, alignItems: "flex-start" }}>
        <KuikaButton type="secondary" size="sm" onClick={(): void => setCount(Math.max(0, count - 1))}>
          −1
        </KuikaButton>
        <KuikaButton type="primary" size="sm" onClick={(): void => setCount(count + 1)}>
          +1
        </KuikaButton>
      </div>
      <KuikaSelectionBar
        count={count}
        actions={
          <KuikaButton type="secondary" size="sm">
            Export
          </KuikaButton>
        }
        onClear={(): void => setCount(0)}
      />
    </div>
  );
};

export const SelectionBarStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="Selection Bar"
      componentName="KuikaSelectionBar"
      sourcePath="ui-kit/kuika-selection-bar"
      description="A floating bulk-action bar that fades in once something is selected and fades out — keeping its last count on screen — the moment the selection clears."
    >
      <SpecRow
        label="Bound to table selection"
        caption="select rows to reveal Export/Delete; Escape or the × clears the selection"
        align="stretch"
      >
        <TableBoundDemo />
      </SpecRow>
      <SpecRow label="Standalone" caption="count driven by +/- buttons, no table involved" align="stretch">
        <StandaloneDemo />
      </SpecRow>
    </ShowcaseFrame>
  );
};
